import { useLocation } from "wouter";
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft } from "lucide-react";
import { useStarterMaterials } from "@/hooks/useStarterMaterials";
import { useStarterCategories } from "@/hooks/useStarterCategories";
import { showError } from "@/lib/toastHelpers";

const UNITS = ["pcs", "pack", "box", "roll", "sheet", "g", "kg", "ml", "l", "tsp", "tbsp", "cup", "oz", "lb", "cm", "m", "ft"];

export default function AddStarterMaterial() {
  const [location, setLocation] = useLocation();
  const [editId, setEditId] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: "",
    categoryId: "",
    unit: "pcs",
    quantity: "",
    purchaseAmount: "",
    notes: "",
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('edit');
    setEditId(id);
  }, [location]);

  const isEditMode = !!editId;

  const { starterMaterials, isLoading: materialsLoading, createMaterialMutation, updateMaterialMutation } = useStarterMaterials({
    onCreateSuccess: () => setLocation("/starter-pack"),
    onUpdateSuccess: () => setLocation("/starter-pack"),
  });

  const { categories: starterMaterialCategories, isLoading: categoriesLoading } = useStarterCategories('materials');

  const editingMaterial = isEditMode
    ? starterMaterials?.find((m: any) => m._id === editId)
    : undefined;

  useEffect(() => {
    if (editingMaterial) {
      setFormData({
        name: editingMaterial.name || "",
        categoryId: editingMaterial.categoryId || "",
        unit: editingMaterial.unit || "pcs",
        quantity: editingMaterial.quantity || "",
        purchaseAmount: editingMaterial.purchaseAmount || "",
        notes: editingMaterial.notes || "",
      });
    }
  }, [editingMaterial]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      showError("Name is required");
      return;
    }

    // Calculate pricePerUnit
    let pricePerUnit: string | null = null;
    if (formData.quantity && formData.purchaseAmount) {
      const qty = parseFloat(formData.quantity);
      const purchase = parseFloat(formData.purchaseAmount);
      if (qty > 0 && purchase > 0) {
        pricePerUnit = (purchase / qty).toFixed(4);
      }
    }

    const data = {
      name: formData.name.trim(),
      categoryId: formData.categoryId || null,
      unit: formData.unit || null,
      quantity: formData.quantity || null,
      purchaseAmount: formData.purchaseAmount || null,
      pricePerUnit,
      notes: formData.notes || null,
    };

    if (isEditMode && editId) {
      updateMaterialMutation.mutate({ id: editId, ...data });
    } else {
      createMaterialMutation.mutate(data);
    }
  };

  if (materialsLoading || categoriesLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (isEditMode && !editingMaterial) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setLocation("/starter-pack")}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-xl sm:text-3xl font-bold tracking-tight">
            {isEditMode ? 'Edit Starter Material' : 'Add Starter Material'}
          </h1>
          <p className="text-muted-foreground">
            {isEditMode ? 'Update starter material details' : 'Create a new starter material template'}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Material Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Material name"
                required
              />
            </div>

            <div>
              <Label htmlFor="category">Category</Label>
              <Select
                value={formData.categoryId}
                onValueChange={(value) => setFormData({ ...formData, categoryId: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {(starterMaterialCategories || []).map((cat: any) => (
                    <SelectItem key={cat._id} value={cat._id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="quantity">Quantity</Label>
                <Input
                  id="quantity"
                  type="number"
                  step="0.001"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  placeholder="e.g. 100"
                />
              </div>
              <div>
                <Label htmlFor="unit">Unit</Label>
                <Select
                  value={formData.unit}
                  onValueChange={(value) => setFormData({ ...formData, unit: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {UNITS.map((u) => (
                      <SelectItem key={u} value={u}>{u}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="purchaseAmount">Purchase Amount</Label>
              <Input
                id="purchaseAmount"
                type="number"
                step="0.01"
                value={formData.purchaseAmount}
                onChange={(e) => setFormData({ ...formData, purchaseAmount: e.target.value })}
                placeholder="0.00"
              />
            </div>

            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Optional notes"
                rows={3}
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setLocation("/starter-pack")}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createMaterialMutation.isPending || updateMaterialMutation.isPending}
              >
                {isEditMode ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
